/**
 * Per-toestel keuze voor hoe gespreksaudio wordt afgespeeld.
 *
 * ── WAAROM DIT EEN INSTELLING IS EN GEEN VASTE KEUZE ────────────────────────
 *
 * Twee Samsung-tablets, dezelfde bundel, dezelfde Android 13: op de SM-X200 is de
 * gespreksaudio hoorbaar, op de SM-X210 niet -- terwijl gemeten is dat de RTP aankomt,
 * decodeert naar echt geluid, en het <audio>-element speelt. De echo-test op datzelfde
 * toestel speelt een BESTAND door hetzelfde soort element wel hoorbaar af, ook met de
 * microfoon open. Het verschil zit dus niet in de audiomodus van Android en niet in de
 * app, maar in hoe dit specifieke model omgaat met de WebRTC-uitgangsstream.
 *
 * Zoiets kun je niet fleet-wide veranderen op een vermoeden. Deze schakelaar laat het
 * per toestel uitproberen -- en omdat de APK zijn inhoud remote van app.caro.care laadt,
 * bereikt hij elke tablet zonder APK-build.
 *
 * ── DE DRIE STANDEN ─────────────────────────────────────────────────────────
 *
 *   default            Wat de app altijd deed. <audio>-element met srcObject.
 *
 *   suspend-webaudio   Bevries tijdens het gesprek de gedeelde AudioContext van de
 *                      beltoon. Die houdt met opzet permanent een Android-uitgangsstream
 *                      open (de keepalive in CallTones), en op sommige HAL's is dat
 *                      precies waarom de tweede stream -- die van WebRTC -- wel samples
 *                      krijgt maar niet hoorbaar is. Beltoon blijft intact: hij wordt
 *                      pas bevroren als er een gesprek ACTIEF is, en meteen hersteld
 *                      bij het einde.
 *
 *   webaudio-render    Speel de stream door een AudioContext-graaf in plaats van door
 *                      het <audio>-element. Een andere renderweg in Chromium, dezelfde
 *                      die de beltoon gebruikt en die op dit toestel aantoonbaar werkt.
 *
 * Standaard `default`: niets verandert tot iemand het bewust omzet.
 */

export type AudioOutputMode = 'default' | 'suspend-webaudio' | 'webaudio-render';

const KEY = 'caro_audio_output_mode';

export const AUDIO_OUTPUT_MODES: Array<{ value: AudioOutputMode; label: string; hint: string }> = [
  {
    value: 'default',
    label: 'Standaard',
    hint: '<audio>-element met srcObject — wat de app altijd deed.',
  },
  {
    value: 'suspend-webaudio',
    label: 'Beltoon-context pauzeren',
    hint: 'Bevriest tijdens het gesprek de AudioContext van de beltoon, zodat WebRTC de '
      + 'uitgang alleen heeft. Herstelt automatisch bij het einde van het gesprek.',
  },
  {
    value: 'webaudio-render',
    label: 'Via Web Audio afspelen',
    hint: 'Speelt de gespreksaudio langs dezelfde weg als de beltoon in plaats van via '
      + 'het <audio>-element.',
  },
];

export function getAudioOutputMode(): AudioOutputMode {
  try {
    const v = localStorage.getItem(KEY);
    if (v === 'suspend-webaudio' || v === 'webaudio-render') return v;
  } catch {
    // Geen opslag beschikbaar (privémodus, gewiste site-data): val terug op standaard.
    // Nooit een uitzondering laten ontsnappen -- dit zit in het pad van een oproep.
  }
  return 'default';
}

export function setAudioOutputMode(mode: AudioOutputMode): void {
  try {
    if (mode === 'default') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, mode);
  } catch {
    // Niet kunnen bewaren is vervelend maar niet fataal: de stand geldt dan alleen
    // tot de app herlaadt.
  }
}
